/**
 * VSEPR-aware positioning of hydrogen atoms.
 *
 * Repositions H atoms bonded to specified parent atoms using VSEPR theory.
 * Total electron domains (bonds + lone pairs) determine the ideal angle
 * between neighbors. H atoms are placed in the largest angular gap among
 * existing non-H bonds.
 *
 * This function does NOT add or remove atoms/bonds — it only updates positions.
 *
 * @packageDocumentation
 */

import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';

export interface LayoutHydrogensOptions {
  /** Bond length for positioning H atoms (distance from parent) */
  bondLength?: number;
}

/**
 * Repositions hydrogen atoms around specified parent atoms using VSEPR geometry.
 *
 * For each parent atom, finds all bonded H atoms and repositions them at
 * `bondLength` distance, spaced according to the total electron domain count
 * (existing bonds + lone pairs). Non-H bonds are treated as fixed reference
 * angles; H atoms fill the remaining angular space.
 *
 * @param workspace - Workspace with H atoms already added and bonded
 * @param parentAtomIds - IDs of atoms whose bonded H atoms should be repositioned
 * @param options - Optional configuration
 * @returns New workspace with H atom positions updated
 */
export function layoutImplicitHydrogens(
  workspace: MolecularWorkspace,
  parentAtomIds: string[],
  options?: LayoutHydrogensOptions
): MolecularWorkspace {
  let ws = workspace;
  const bl = options?.bondLength ?? 1.5;

  for (const parentAtomId of parentAtomIds) {
    const parentAtom = ws.getAtom(parentAtomId);
    if (!parentAtom) continue;

    // Find existing bond angles and identify bonded H atoms to reposition
    const existingAngles: number[] = [];
    const hAtomIds: string[] = [];

    for (const bond of ws.getAllBonds()) {
      let neighborId: string | undefined;
      if (bond.a1.id === parentAtomId) neighborId = bond.a2.id;
      else if (bond.a2.id === parentAtomId) neighborId = bond.a1.id;
      if (!neighborId) continue;

      const neighbor = ws.getAtom(neighborId);
      if (!neighbor) continue;

      if (neighbor.symbol === 'H') {
        hAtomIds.push(neighborId);
      } else {
        existingAngles.push(Math.atan2(neighbor.y - parentAtom.y, neighbor.x - parentAtom.x));
      }
    }

    if (hAtomIds.length === 0) continue;

    // Total electron domains = non-H bonds + H bonds + lone pairs
    const totalDomains = existingAngles.length + hAtomIds.length + parentAtom.lonePairs;

    // VSEPR ideal angle between domains
    const domainAngle = totalDomains > 1 ? (2 * Math.PI) / totalDomains : 0;

    // Determine starting angle for H placement
    let startAngle: number;
    if (existingAngles.length > 0) {
      // Find the largest angular gap among existing non-H bonds
      const sorted = [...existingAngles].sort((a, b) => a - b);
      let bestGapStart = sorted[0];
      let bestGapSize = 0;
      for (let i = 0; i < sorted.length; i++) {
        const next = i + 1 < sorted.length ? sorted[i + 1] : sorted[0] + 2 * Math.PI;
        const gap = next - sorted[i];
        if (gap > bestGapSize) {
          bestGapSize = gap;
          bestGapStart = sorted[i];
        }
      }
      startAngle = bestGapStart + domainAngle;
    } else {
      startAngle = 0;
    }

    // Reposition each H atom
    for (let i = 0; i < hAtomIds.length; i++) {
      const angle = startAngle + i * domainAngle;
      ws = ws.updateAtomPosition(
        hAtomIds[i],
        parentAtom.x + bl * Math.cos(angle),
        parentAtom.y + bl * Math.sin(angle)
      );
    }
  }

  return ws;
}
